/**
 * `fetch` that gives up.
 *
 * A request on hotel wifi does not always fail: sometimes it just never
 * answers. Nothing here had a timeout, so a single hung read held the gallery
 * on its loading skeleton for as long as the tab stayed open, with nothing in
 * the console to say why (the first load's error was swallowed, and a hang has
 * no error at all). Every API read and the token refresh in front of them go
 * through this now, so a stall becomes an error the page can show and retry.
 *
 * Uploads do not: they are XHRs with their own progress and cancel, and a slow
 * upload is still a working one.
 */

/** Long enough for a gallery page over a poor connection (~130 kB at a few
 *  hundred kB/s is well under a second), short enough that a guest is not left
 *  watching a skeleton. The slowest `/photos` read in production is under 1s. */
export const REQUEST_TIMEOUT_MS = 20_000;

export class RequestTimeout extends Error {
  constructor(readonly url: string, readonly ms: number) {
    super(`No answer from ${new URL(url, 'https://x').pathname} in ${Math.round(ms / 1000)}s`);
    this.name = 'RequestTimeout';
  }
}

/**
 * `fetch(url, init)` and then `read(response)`, the two together rejected with
 * `RequestTimeout` if they have not finished within `ms`. A caller's own
 * `signal` still cancels it, and that rejection is the caller's `AbortError`,
 * not a timeout.
 *
 * **The body read is inside the deadline, not after it.** `fetch` resolves as
 * soon as the headers arrive, so a timer cleared there would let a response
 * that starts and then stalls mid-body hang exactly as before. Aborting the
 * controller cancels the body stream too, so `read` rejects instead of waiting.
 *
 * Built by hand rather than with `AbortSignal.any`/`AbortSignal.timeout`, which
 * arrived in Safari only in 17.4 and 16 respectively — and older iPhones are
 * exactly the guests on the worst connections.
 */
export async function requestWithTimeout<T>(
  url: string,
  init: RequestInit,
  read: (res: Response) => Promise<T>,
  ms: number = REQUEST_TIMEOUT_MS,
): Promise<T> {
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => { timedOut = true; controller.abort(); }, ms);

  const outer = init.signal;
  const forward = () => controller.abort();
  if (outer) {
    if (outer.aborted) controller.abort();
    else outer.addEventListener('abort', forward, { once: true });
  }

  try {
    const res = await fetch(url, { ...init, signal: controller.signal });
    return await read(res);
  } catch (err) {
    if (timedOut) throw new RequestTimeout(url, ms);
    throw err;
  } finally {
    clearTimeout(timer);
    outer?.removeEventListener('abort', forward);
  }
}
